import type { PlaybackTokenRequest } from './apiContracts';
import { PrototypeApiError } from './apiTransport';

const STORAGE_KEY = 'deepmox.playback.authorization-context.v1';
const RESPONSE_LOSS_RETRY_MS = 12_000;

type ContextState = { ownerId: string; ownerVersion?: number };
let memory: ContextState | null = null;
const requestIds = new Map<string, { requestId: string; retryUntil: number }>();

function state(): ContextState {
	if (memory) return memory;
	try {
		const stored = JSON.parse(sessionStorage.getItem(STORAGE_KEY) ?? '') as Partial<ContextState>;
		if (typeof stored.ownerId === 'string' && stored.ownerId) return (memory = stored as ContextState);
	} catch { /* storage is optional */ }
	memory = { ownerId: crypto.randomUUID() };
	return memory;
}

export function withPlaybackAuthorization(input: PlaybackTokenRequest, mediaKind: 'audio' | 'video'): Record<string, unknown> {
	const current = state();
	const requestKey = playbackRequestKey(current, input, mediaKind);
	const existing = requestIds.get(requestKey);
	const requestId = input.requestId ?? (existing && existing.retryUntil > Date.now() ? existing.requestId : crypto.randomUUID());
	requestIds.set(requestKey, { requestId, retryUntil: Date.now() + RESPONSE_LOSS_RETRY_MS });
	return {
		topicId: input.topicId,
		chapterIndex: input.chapterIndex,
		plan: input.plan,
		requestId,
		authorization: {
			ownerId: current.ownerId,
			...(typeof current.ownerVersion === 'number' ? { ownerVersion: current.ownerVersion } : {}),
			role: input.playbackRole ?? 'current',
		},
	};
}

/** A normal response makes the idempotency key spent. A transport failure is
 * retained briefly because the Worker may have completed after the socket died. */
export function settlePlaybackTokenRequest(input: PlaybackTokenRequest, mediaKind: 'audio' | 'video', outcome: 'success' | 'terminal-error' | 'response-lost'): void {
	const key = playbackRequestKey(state(), input, mediaKind);
	if (outcome === 'response-lost') {
		const pending = requestIds.get(key);
		if (pending) pending.retryUntil = Date.now() + RESPONSE_LOSS_RETRY_MS;
		return;
	}
	requestIds.delete(key);
}

/** Login/logout must never reuse a key or owner fence from a prior identity. */
export function resetPlaybackAuthorizationContext(): void {
	requestIds.clear();
	memory = null;
	try { sessionStorage.removeItem(STORAGE_KEY); } catch { /* storage is optional */ }
}

/**
 * A 409 owner fence means another tab has explicitly claimed this playback
 * context. Call this only from an explicit user play/resume action: it mints
 * one new owner identity and makes one bounded retry, never a background
 * recovery loop.
 */
export async function requestExplicitPlaybackTokenWithOwnerRecovery<T>(request: () => Promise<T>): Promise<T> {
	try {
		return await request();
	} catch (error) {
		if (!isPlaybackOwnerStale(error)) throw error;
		resetPlaybackAuthorizationContext();
		return request();
	}
}

export function isPlaybackOwnerStale(error: unknown): error is PrototypeApiError {
	if (!(error instanceof PrototypeApiError) || error.status !== 409 || !error.body || typeof error.body !== 'object') return false;
	return (error.body as { error?: unknown }).error === 'playback_owner_stale';
}

function playbackRequestKey(current: ContextState, input: PlaybackTokenRequest, mediaKind: 'audio' | 'video'): string {
	return `${current.ownerId}:${mediaKind}:${input.playbackRole ?? 'current'}:${input.topicId}:${input.chapterIndex}`;
}

export function acceptPlaybackAuthorization(response: unknown): void {
	const version = (response as { authorization?: { ownerVersion?: unknown } } | null)?.authorization?.ownerVersion;
	if (!Number.isInteger(version) || (version as number) < 1) return;
	const current = state();
	if (current.ownerVersion === version) return;
	memory = { ...current, ownerVersion: version as number };
	try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(memory)); } catch { /* storage is optional */ }
}
